/**
 * The inline editor a session note's body swaps into, shared by the two pages
 * that can correct a note: the member's Session-notes card and the trainer's
 * authored-notes table.
 *
 * Shared as a COMPONENT and not just as decision logic (2026-08-06). The rules
 * about when a draft may be saved already live in `lib/session-note-edit.ts`;
 * this is the other half — the control surface those rules govern. Two copies of
 * it drifted apart the moment one page grew a feature (a character counter, an
 * autosave, a keyboard shortcut) and the other did not, and the operator would
 * meet two different editors for the same row in the same database.
 *
 * Save is disabled on an unchanged or empty body: `edit_session_note` bounds
 * `body` at 1..4000, and dispatching a no-op edit would file an audit row
 * recording a change that did not happen — for a note, that audit row is the
 * only history there is.
 *
 * `maxLength` mirrors the action's upper bound purely so the operator is stopped
 * at the limit instead of collecting an `input_invalid`; the dispatch chokepoint
 * remains the sole enforcement layer (invariant 2).
 */

import { useState } from "react";
import {
  canSaveSessionNote,
  sessionNoteSaveBody,
  SESSION_NOTE_MAX_LENGTH,
} from "../lib/session-note-edit";

export function SessionNoteEditor({
  initialBody,
  busy,
  onCancel,
  onSave,
}: {
  readonly initialBody: string;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: (body: string) => void;
}): JSX.Element {
  const [draft, setDraft] = useState(initialBody);
  const canSave = canSaveSessionNote(draft, initialBody);
  return (
    <div className="session-note-editor">
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        maxLength={SESSION_NOTE_MAX_LENGTH}
        rows={4}
        disabled={busy}
        aria-label="Note text"
      />
      <div className="session-note-editor-actions">
        <button
          type="button"
          className="session-note-save"
          disabled={busy || !canSave}
          onClick={() => onSave(sessionNoteSaveBody(draft))}
        >
          {busy ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="session-note-cancel"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
